import { useState, useMemo, Fragment } from "react";
import { useLayout } from "@/hooks/use-layout";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Download, CheckCircle2, RotateCcw, Clock, AlertTriangle, FileSearch, Filter, ChevronDown, ChevronRight, Send, CalendarPlus, CalendarCheck, CalendarX, MapPin } from "lucide-react";
import type { ActionItemRecord, MdMeetingState } from "@shared/v2/types";

type QueueItem = ActionItemRecord & {
  received_at: string | null;
  holding_seconds: number | null;
};

type ActiveUser = { id: string; username: string; name: string; email: string | null };

function formatMeetingWhen(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function meetingDateTimeParts(iso: string | null | undefined): { date: string; time: string } {
  if (!iso) return { date: "", time: "" };
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return {
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    };
  } catch {
    return { date: "", time: "" };
  }
}

type QueueResponse = {
  data: QueueItem[];
  as_of: string;
  caller_name: string;
  can_act: boolean;
};

type DispatchItem = ActionItemRecord & {
  md_approved_at: string | null;
  md_approved_by: string | null;
  held_working_days: number;
  recipient_lane_display: string;
  dispatch_due_at: string | null;
  sla_state: "green" | "amber" | "red";
  sla_days_remaining: number;
  sla_days: number;
};

type DispatchResponse = {
  data: DispatchItem[];
  as_of: string;
  target_working_days: number;
  sla_working_days: number;
  caller_name: string;
  can_mark_dispatched: boolean;
};

type DueBucket = "all" | "overdue" | "due_7" | "due_30" | "later";
type PriorityFilter = "all" | "high" | "medium" | "low";
type Tab = "review" | "dispatch";

function priorityClass(p: string | null): string {
  const v = (p || "").toLowerCase();
  if (v === "high") return "bg-red-500/20 text-red-300 border-red-500/30";
  if (v === "medium") return "bg-amber-500/20 text-amber-300 border-amber-500/30";
  if (v === "low") return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
  return "bg-white/10 text-white/70 border-white/20";
}

function formatHolding(seconds: number | null): string {
  if (seconds == null) return "\u2014";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h`;
  return "<1h";
}

function dueBucketOf(dueDate: string | null): DueBucket {
  if (!dueDate) return "later";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((due.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return "overdue";
  if (diffDays <= 7) return "due_7";
  if (diffDays <= 30) return "due_30";
  return "later";
}

function dispatchAgeClass(held: number, target: number): string {
  if (held <= target) return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
  if (held <= target + 2) return "bg-amber-500/20 text-amber-300 border-amber-500/30";
  return "bg-red-500/20 text-red-300 border-red-500/30";
}

function slaStateClass(state: "green" | "amber" | "red"): string {
  if (state === "red") return "bg-red-500/20 text-red-300 border-red-500/30";
  if (state === "amber") return "bg-amber-500/20 text-amber-300 border-amber-500/30";
  return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
}

function slaStateLabel(state: "green" | "amber" | "red", daysRemaining: number, slaDays: number): string {
  if (state === "red") {
    const overdue = Math.abs(daysRemaining);
    return `SLA breached • ${overdue}d late`;
  }
  if (state === "amber") {
    return `SLA warning • ${daysRemaining}d left`;
  }
  return `SLA on track • ${daysRemaining}/${slaDays}d`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function MdMeetingsPage() {
  const { containerClass } = useLayout();
  const { toast } = useToast();
  const { canActOnMdReview, canMarkDispatched, canViewAwaitingDispatch, teamMemberName } = useAuth();

  const [tab, setTab] = useState<Tab>("review");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dueFilter, setDueFilter] = useState<DueBucket>("all");
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>("all");
  const [onlyMine, setOnlyMine] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNote, setApproveNote] = useState("");

  const [returnOpen, setReturnOpen] = useState(false);
  const [returnTargetId, setReturnTargetId] = useState<string | null>(null);
  const [returnReason, setReturnReason] = useState("");

  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [dispatchTarget, setDispatchTarget] = useState<DispatchItem | null>(null);
  const [dispatchRecipient, setDispatchRecipient] = useState("");
  const [dispatchDate, setDispatchDate] = useState(todayIso());
  const [dispatchNote, setDispatchNote] = useState("");

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState<QueueItem | null>(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduleLocation, setScheduleLocation] = useState("");
  const [scheduleAttendeeIds, setScheduleAttendeeIds] = useState<Set<string>>(new Set());
  const [scheduleNote, setScheduleNote] = useState("");

  const [holdOpen, setHoldOpen] = useState(false);
  const [holdTarget, setHoldTarget] = useState<QueueItem | null>(null);
  const [holdNote, setHoldNote] = useState("");

  const [cancelMeetingOpen, setCancelMeetingOpen] = useState(false);
  const [cancelMeetingTarget, setCancelMeetingTarget] = useState<QueueItem | null>(null);
  const [cancelMeetingNote, setCancelMeetingNote] = useState("");

  const queueQuery = useQuery<QueueResponse>({ queryKey: ["/api/md-review-queue"] });
  const activeUsersQuery = useQuery<{ data: ActiveUser[] }>({
    queryKey: ["/api/users/active"],
    enabled: canActOnMdReview,
  });
  const activeUsers = activeUsersQuery.data?.data ?? [];
  const dispatchQuery = useQuery<DispatchResponse>({
    queryKey: ["/api/awaiting-dispatch-queue"],
    enabled: canViewAwaitingDispatch,
  });

  const items = queueQuery.data?.data ?? [];
  const callerName = queueQuery.data?.caller_name ?? teamMemberName ?? "";
  const isActualMd = callerName === "SDF MD";

  const dispatchItems = dispatchQuery.data?.data ?? [];
  const dispatchTarget_days = dispatchQuery.data?.target_working_days ?? 2;

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (priorityFilter !== "all" && (item.priority || "").toLowerCase() !== priorityFilter) return false;
      if (dueFilter !== "all" && dueBucketOf(item.due_date) !== dueFilter) return false;
      if (onlyMine && isActualMd && item.assigned_to !== callerName && item.current_responsible !== callerName) {
        return false;
      }
      return true;
    });
  }, [items, priorityFilter, dueFilter, onlyMine, isActualMd, callerName]);

  const allFilteredSelected = filteredItems.length > 0 && filteredItems.every((i) => selectedIds.has(i.id));

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredItems.forEach((i) => next.delete(i.id));
      } else {
        filteredItems.forEach((i) => next.add(i.id));
      }
      return next;
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/md-review-queue"] });
    queryClient.invalidateQueries({ queryKey: ["/api/awaiting-dispatch-queue"] });
    queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
  };

  const bulkApproveMutation = useMutation({
    mutationFn: async (payload: { ids: string[]; note?: string }) => {
      const res = await apiRequest("POST", "/api/md-review-queue/bulk-approve", payload);
      return res.json();
    },
    onSuccess: (resp: any) => {
      const result = resp?.data;
      if (result) {
        toast({
          title: "Approval processed",
          description: `${result.succeeded} of ${result.processed} item(s) processed${result.failed ? `; ${result.failed} failed` : ""}. Items routed to dispatch or closed based on lane.`,
        });
      } else {
        toast({ title: "Approval submitted" });
      }
      setSelectedIds(new Set());
      setApproveOpen(false);
      setApproveNote("");
      invalidateAll();
    },
    onError: (err: any) => {
      toast({ variant: "destructive", title: "Approval failed", description: err?.message || "Could not approve items." });
    },
  });

  const returnMutation = useMutation({
    mutationFn: async (payload: { id: string; reason: string }) => {
      const res = await apiRequest("POST", `/api/action-items/${payload.id}/md-return`, { reason: payload.reason });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Item returned", description: "The item is back with the original assignee." });
      setReturnOpen(false);
      setReturnReason("");
      setReturnTargetId(null);
      invalidateAll();
    },
    onError: (err: any) => {
      toast({ variant: "destructive", title: "Return failed", description: err?.message || "Could not return item." });
    },
  });

  const dispatchMutation = useMutation({
    mutationFn: async (payload: { id: string; recipient: string; note?: string; dispatchedAt: string }) => {
      const res = await apiRequest("POST", `/api/action-items/${payload.id}/dispatch-upward`, {
        recipient: payload.recipient,
        note: payload.note,
        dispatchedAt: payload.dispatchedAt,
      });
      return res.json();
    },
    onSuccess: (resp: any) => {
      const d = resp?.data;
      const onTime = d?.on_time;
      toast({
        title: "Marked dispatched",
        description: d
          ? `Sent upward in ${d.lag_working_days} working day(s) (${onTime ? "on time" : "late"}).`
          : "Item marked as sent upward.",
      });
      setDispatchOpen(false);
      setDispatchTarget(null);
      setDispatchRecipient("");
      setDispatchNote("");
      setDispatchDate(todayIso());
      invalidateAll();
    },
    onError: (err: any) => {
      toast({ variant: "destructive", title: "Dispatch failed", description: err?.message || "Could not mark dispatched." });
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: async (payload: {
      id: string;
      scheduledFor: string;
      location: string | null;
      attendees: Array<{ userId: string; name: string; email: string | null }>;
      note?: string;
    }) => {
      const res = await apiRequest("POST", `/api/action-items/${payload.id}/md-meeting/schedule`, {
        scheduledFor: payload.scheduledFor,
        location: payload.location,
        attendees: payload.attendees,
        note: payload.note,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Meeting saved", description: "Attendees with email on file have been notified." });
      setScheduleOpen(false);
      setScheduleTarget(null);
      setScheduleDate("");
      setScheduleTime("");
      setScheduleLocation("");
      setScheduleAttendeeIds(new Set());
      setScheduleNote("");
      invalidateAll();
    },
    onError: (err: any) => {
      toast({ variant: "destructive", title: "Could not save meeting", description: err?.message || "Please try again." });
    },
  });

  const markHeldMutation = useMutation({
    mutationFn: async (payload: { id: string; note?: string }) => {
      const res = await apiRequest("POST", `/api/action-items/${payload.id}/md-meeting/mark-held`, { note: payload.note });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Meeting marked as held" });
      setHoldOpen(false);
      setHoldTarget(null);
      setHoldNote("");
      invalidateAll();
    },
    onError: (err: any) => {
      toast({ variant: "destructive", title: "Could not mark held", description: err?.message || "Please try again." });
    },
  });

  const cancelMeetingMutation = useMutation({
    mutationFn: async (payload: { id: string; note?: string }) => {
      const res = await apiRequest("POST", `/api/action-items/${payload.id}/md-meeting/cancel`, { note: payload.note });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Meeting cancelled", description: "Attendees with email on file have been notified." });
      setCancelMeetingOpen(false);
      setCancelMeetingTarget(null);
      setCancelMeetingNote("");
      invalidateAll();
    },
    onError: (err: any) => {
      toast({ variant: "destructive", title: "Could not cancel meeting", description: err?.message || "Please try again." });
    },
  });

  const openSchedule = (item: QueueItem) => {
    setScheduleTarget(item);
    const existing = item.md_meeting_state ?? null;
    const parts = meetingDateTimeParts(existing?.scheduledFor);
    setScheduleDate(parts.date);
    setScheduleTime(parts.time);
    setScheduleLocation(existing?.location || "");
    setScheduleNote("");
    const initial = new Set<string>();
    if (existing?.attendees && existing.attendees.length > 0) {
      existing.attendees.forEach((a) => initial.add(a.userId));
    } else {
      const owner = activeUsers.find((u) => u.name === item.assigned_to);
      if (owner) initial.add(owner.id);
      const md = activeUsers.find((u) => u.name === "SDF MD");
      if (md) initial.add(md.id);
    }
    setScheduleAttendeeIds(initial);
    setScheduleOpen(true);
  };

  const openMarkHeld = (item: QueueItem) => {
    setHoldTarget(item);
    setHoldNote("");
    setHoldOpen(true);
  };

  const openCancelMeeting = (item: QueueItem) => {
    setCancelMeetingTarget(item);
    setCancelMeetingNote("");
    setCancelMeetingOpen(true);
  };

  const toggleAttendee = (id: string) => {
    setScheduleAttendeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const downloadAgenda = async () => {
    try {
      let tzParam = "";
      try { tzParam = `?tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`; } catch {}
      const res = await fetch(`/api/md-review-queue/agenda.pdf${tzParam}`, { credentials: "include" });
      if (!res.ok) {
        const msg = await res.text().catch(() => "Failed to download agenda");
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sdf-md-agenda-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast({ variant: "destructive", title: "Download failed", description: err?.message || "Could not download agenda." });
    }
  };

  const openApprove = () => {
    if (selectedIds.size === 0) {
      toast({ variant: "destructive", title: "Nothing selected", description: "Pick at least one item to approve." });
      return;
    }
    setApproveOpen(true);
  };

  const openReturn = (id: string) => {
    setReturnTargetId(id);
    setReturnReason("");
    setReturnOpen(true);
  };

  const openDispatch = (item: DispatchItem) => {
    setDispatchTarget(item);
    setDispatchRecipient(`${item.recipient_lane_display} via email`);
    setDispatchDate(todayIso());
    setDispatchNote("");
    setDispatchOpen(true);
  };

  const tabBtnClass = (active: boolean) =>
    `px-4 py-2 text-sm font-medium rounded-md transition-colors ${
      active
        ? "bg-cyan-500/20 text-cyan-200 border border-cyan-500/40"
        : "bg-white/5 text-white/60 border border-white/10 hover:text-white hover:bg-white/10"
    }`;

  return (
    <div className={`${containerClass} mx-auto px-6 py-8`}>
      <div className="space-y-4">
        <Card className="bg-[#0d1b3e] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle className="text-white text-xl flex items-center gap-2">
                <FileSearch className="w-5 h-5 text-cyan-400" />
                SDF MD Meetings
              </CardTitle>
              <p className="text-white/50 text-sm mt-1">
                {tab === "review"
                  ? "Items currently with the SDF MD for review. Sorted by received order (oldest first)."
                  : `Items the MD approved that still need to be sent upward. Target: ${dispatchTarget_days} working day(s).`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={downloadAgenda} data-testid="button-download-agenda">
                <Download className="w-4 h-4 mr-2" />
                Agenda PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <button
                className={tabBtnClass(tab === "review")}
                onClick={() => setTab("review")}
                data-testid="tab-review-queue"
              >
                Review Queue
                <span className="ml-2 text-xs text-white/50">({items.length})</span>
              </button>
              {canViewAwaitingDispatch && (
                <button
                  className={tabBtnClass(tab === "dispatch")}
                  onClick={() => setTab("dispatch")}
                  data-testid="tab-awaiting-dispatch"
                >
                  Awaiting Dispatch
                  <span className="ml-2 text-xs text-white/50">({dispatchItems.length})</span>
                </button>
              )}
            </div>

            {tab === "review" && (
              <Fragment>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2 text-white/60 text-sm">
                    <Filter className="w-4 h-4" />
                    Filters:
                  </div>
                  <Select value={dueFilter} onValueChange={(v) => setDueFilter(v as DueBucket)}>
                    <SelectTrigger className="w-[160px] bg-white/5 border-white/10 text-white" data-testid="select-due-bucket">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All due dates</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                      <SelectItem value="due_7">Due in 7 days</SelectItem>
                      <SelectItem value="due_30">Due in 30 days</SelectItem>
                      <SelectItem value="later">Later / no date</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={priorityFilter} onValueChange={(v) => setPriorityFilter(v as PriorityFilter)}>
                    <SelectTrigger className="w-[140px] bg-white/5 border-white/10 text-white" data-testid="select-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All priorities</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                  {isActualMd && (
                    <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                      <Checkbox
                        checked={onlyMine}
                        onCheckedChange={(v) => setOnlyMine(v === true)}
                        data-testid="checkbox-only-mine"
                      />
                      Show only items assigned to me
                    </label>
                  )}
                  <div className="ml-auto text-white/50 text-sm" data-testid="text-queue-count">
                    {filteredItems.length} of {items.length} item(s)
                  </div>
                </div>

                {canActOnMdReview && (
                  <div className="flex flex-wrap items-center gap-2 p-3 rounded-md bg-white/5 border border-white/10">
                    <span className="text-white/70 text-sm">{selectedIds.size} selected</span>
                    <span className="text-white/40 text-xs">
                      Sign-off (when required) and lane routing are handled automatically.
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={openApprove}
                        disabled={selectedIds.size === 0 || bulkApproveMutation.isPending}
                        data-testid="button-bulk-approve"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Approve & Close
                      </Button>
                    </div>
                  </div>
                )}

                {queueQuery.isLoading ? (
                  <div className="flex items-center justify-center py-12 text-white/50">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Loading queue...
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="text-center py-12 text-white/50" data-testid="text-empty-queue">
                    {items.length === 0
                      ? "No items currently with the SDF MD."
                      : "No items match the current filters."}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-md border border-white/10">
                    <table className="w-full text-sm">
                      <thead className="bg-white/5 text-white/60">
                        <tr>
                          {canActOnMdReview && (
                            <th className="p-3 text-left w-10">
                              <Checkbox
                                checked={allFilteredSelected}
                                onCheckedChange={toggleAll}
                                data-testid="checkbox-select-all"
                              />
                            </th>
                          )}
                          <th className="p-3 text-left w-8"></th>
                          <th className="p-3 text-left">Title</th>
                          <th className="p-3 text-left">Owner</th>
                          <th className="p-3 text-left">Priority</th>
                          <th className="p-3 text-left">Due</th>
                          <th className="p-3 text-left">Held</th>
                          {canActOnMdReview && <th className="p-3 text-right">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredItems.map((item) => {
                          const expanded = expandedIds.has(item.id);
                          const overdue = item.due_date && new Date(item.due_date) < new Date();
                          return (
                            <Fragment key={item.id}>
                              <tr
                                className="border-t border-white/5 hover:bg-white/5"
                                data-testid={`row-md-queue-${item.id}`}
                              >
                                {canActOnMdReview && (
                                  <td className="p-3">
                                    <Checkbox
                                      checked={selectedIds.has(item.id)}
                                      onCheckedChange={() => toggleOne(item.id)}
                                      data-testid={`checkbox-select-${item.id}`}
                                    />
                                  </td>
                                )}
                                <td className="p-3">
                                  <button
                                    onClick={() => toggleExpand(item.id)}
                                    className="text-white/40 hover:text-white"
                                    data-testid={`button-expand-${item.id}`}
                                  >
                                    {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                  </button>
                                </td>
                                <td className="p-3 text-white" data-testid={`text-title-${item.id}`}>
                                  <div className="font-medium">{item.title}</div>
                                  {item.meeting_name && (
                                    <div className="text-xs text-white/40 mt-0.5">{item.meeting_name}</div>
                                  )}
                                  {item.md_meeting_state && (
                                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid={`meeting-pill-${item.id}`}>
                                      <Badge
                                        variant="outline"
                                        className={
                                          item.md_meeting_state.status === "held"
                                            ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-sm gap-1"
                                            : "bg-cyan-500/15 text-cyan-200 border-cyan-500/30 text-sm gap-1"
                                        }
                                      >
                                        {item.md_meeting_state.status === "held" ? <CalendarCheck className="w-3 h-3" /> : <CalendarPlus className="w-3 h-3" />}
                                        📅 {formatMeetingWhen(item.md_meeting_state.scheduledFor)}
                                        {item.md_meeting_state.status === "held" && " (held)"}
                                      </Badge>
                                      {item.md_meeting_state.location && (
                                        <Badge variant="outline" className="bg-white/5 text-white/60 border-white/10 text-sm gap-1">
                                          <MapPin className="w-3 h-3" />
                                          {item.md_meeting_state.location}
                                        </Badge>
                                      )}
                                      {item.md_meeting_state.rescheduleCount > 0 && (
                                        <Badge variant="outline" className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-sm">
                                          Rescheduled {item.md_meeting_state.rescheduleCount}x
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="p-3 text-white/80" data-testid={`text-owner-${item.id}`}>
                                  {item.assigned_to || "\u2014"}
                                </td>
                                <td className="p-3">
                                  <Badge variant="outline" className={priorityClass(item.priority)} data-testid={`badge-priority-${item.id}`}>
                                    {item.priority || "\u2014"}
                                  </Badge>
                                </td>
                                <td className="p-3" data-testid={`text-due-${item.id}`}>
                                  {item.due_date ? (
                                    <span className={overdue ? "text-red-400 flex items-center gap-1" : "text-white/80"}>
                                      {overdue && <AlertTriangle className="w-3 h-3" />}
                                      {item.due_date}
                                    </span>
                                  ) : (
                                    <span className="text-white/40">&mdash;</span>
                                  )}
                                </td>
                                <td className="p-3 text-white/70" data-testid={`text-held-${item.id}`}>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3 text-white/40" />
                                    {formatHolding(item.holding_seconds)}
                                  </span>
                                </td>
                                {canActOnMdReview && (
                                  <td className="p-3 text-right">
                                    <div className="flex items-center justify-end gap-1 flex-wrap">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => openSchedule(item)}
                                        data-testid={`button-schedule-${item.id}`}
                                        title={item.md_meeting_state ? "Reschedule meeting" : "Schedule meeting"}
                                      >
                                        <CalendarPlus className="w-3 h-3 mr-1" />
                                        {item.md_meeting_state ? "Reschedule" : "Schedule"}
                                      </Button>
                                      {item.md_meeting_state && item.md_meeting_state.status === "scheduled" && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => openMarkHeld(item)}
                                          data-testid={`button-mark-held-${item.id}`}
                                          title="Mark meeting as held"
                                        >
                                          <CalendarCheck className="w-3 h-3 mr-1" />
                                          Held
                                        </Button>
                                      )}
                                      {item.md_meeting_state && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => openCancelMeeting(item)}
                                          data-testid={`button-cancel-meeting-${item.id}`}
                                          title="Cancel meeting"
                                        >
                                          <CalendarX className="w-3 h-3 mr-1" />
                                          Cancel
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => openReturn(item.id)}
                                        data-testid={`button-return-${item.id}`}
                                      >
                                        <RotateCcw className="w-3 h-3 mr-1" />
                                        Return
                                      </Button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                              {expanded && (
                                <tr key={`${item.id}-detail`} className="border-t border-white/5 bg-white/[0.02]">
                                  <td colSpan={canActOnMdReview ? 8 : 6} className="p-4">
                                    <div className="text-xs uppercase tracking-wide text-white/40 mb-1">Instruction</div>
                                    <p className="text-sm text-white/80 whitespace-pre-wrap" data-testid={`text-instruction-${item.id}`}>
                                      {item.instruction || "(no instruction)"}
                                    </p>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Fragment>
            )}

            {tab === "dispatch" && (
              <Fragment>
                <div className="text-white/60 text-xs">
                  Target: dispatch within <strong className="text-white/80">{dispatchQuery.data?.sla_working_days ?? dispatchTarget_days}</strong> working day(s) of MD approval (SLA configured in System Settings).
                  {!canMarkDispatched && (
                    <span className="ml-2 text-amber-300/80">Read-only — only PMO, Board Secretary, MD admins, or super admins can mark dispatched.</span>
                  )}
                </div>
                {dispatchQuery.isLoading ? (
                  <div className="flex items-center justify-center py-12 text-white/50">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Loading awaiting-dispatch list...
                  </div>
                ) : dispatchItems.length === 0 ? (
                  <div className="text-center py-12 text-white/50" data-testid="text-empty-dispatch">
                    Nothing awaiting dispatch right now.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-md border border-white/10">
                    <table className="w-full text-sm">
                      <thead className="bg-white/5 text-white/60">
                        <tr>
                          <th className="p-3 text-left">Title</th>
                          <th className="p-3 text-left">Owner</th>
                          <th className="p-3 text-left">Recipient Lane</th>
                          <th className="p-3 text-left">MD Approved</th>
                          <th className="p-3 text-left">Held (working days)</th>
                          <th className="p-3 text-left">SLA</th>
                          {canMarkDispatched && <th className="p-3 text-right">Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {dispatchItems.map((item) => (
                          <tr key={item.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`row-dispatch-${item.id}`}>
                            <td className="p-3 text-white">
                              <div className="font-medium" data-testid={`text-dispatch-title-${item.id}`}>{item.title}</div>
                              {item.meeting_name && (
                                <div className="text-xs text-white/40 mt-0.5">{item.meeting_name}</div>
                              )}
                            </td>
                            <td className="p-3 text-white/80">{item.assigned_to || "\u2014"}</td>
                            <td className="p-3 text-white/80" data-testid={`text-recipient-lane-${item.id}`}>
                              {item.recipient_lane_display}
                            </td>
                            <td className="p-3 text-white/70" data-testid={`text-md-approved-${item.id}`}>
                              {item.md_approved_at ? item.md_approved_at.slice(0, 10) : "\u2014"}
                              {item.md_approved_by && (
                                <div className="text-xs text-white/40">by {item.md_approved_by}</div>
                              )}
                            </td>
                            <td className="p-3">
                              <Badge
                                variant="outline"
                                className={dispatchAgeClass(item.held_working_days, dispatchTarget_days)}
                                data-testid={`badge-held-${item.id}`}
                              >
                                {item.held_working_days} day(s)
                                {item.held_working_days > dispatchTarget_days && " — late"}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <Badge
                                variant="outline"
                                className={slaStateClass(item.sla_state)}
                                data-testid={`badge-sla-${item.id}`}
                                title={item.dispatch_due_at ? `Dispatch due by ${item.dispatch_due_at.slice(0, 10)}` : undefined}
                              >
                                {slaStateLabel(item.sla_state, item.sla_days_remaining, item.sla_days)}
                              </Badge>
                            </td>
                            {canMarkDispatched && (
                              <td className="p-3 text-right">
                                <Button
                                  size="sm"
                                  onClick={() => openDispatch(item)}
                                  data-testid={`button-mark-dispatched-${item.id}`}
                                >
                                  <Send className="w-3 h-3 mr-1" />
                                  Mark Dispatched
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Fragment>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="bg-[#0d1b3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Approve & close items</DialogTitle>
            <DialogDescription className="text-white/60">
              {selectedIds.size} item(s) will be processed. Items from above-MD lanes (EDGE Chairman, EDGE MD Office, Board / ExCom) will move to <strong>Awaiting Dispatch</strong>; items from the SDF MD lane will move to <strong>Closed</strong>. Sign-off is recorded automatically when required.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-white/70">Shared note (optional)</label>
            <Textarea
              value={approveNote}
              onChange={(e) => setApproveNote(e.target.value)}
              placeholder="Optional note recorded against each item and included in the notification."
              className="bg-white/5 border-white/10 text-white"
              data-testid="textarea-approve-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)} data-testid="button-approve-cancel">
              Cancel
            </Button>
            <Button
              onClick={() =>
                bulkApproveMutation.mutate({
                  ids: Array.from(selectedIds),
                  note: approveNote.trim() || undefined,
                })
              }
              disabled={bulkApproveMutation.isPending}
              data-testid="button-approve-confirm"
            >
              {bulkApproveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={returnOpen} onOpenChange={setReturnOpen}>
        <DialogContent className="bg-[#0d1b3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Return for revision</DialogTitle>
            <DialogDescription className="text-white/60">
              The item will go back to the original assignee with the reason below. They will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-white/70">Reason (required)</label>
            <Textarea
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder="Explain what needs to be revised before re-submission."
              className="bg-white/5 border-white/10 text-white"
              data-testid="textarea-return-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnOpen(false)} data-testid="button-return-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!returnTargetId || !returnReason.trim()) {
                  toast({ variant: "destructive", title: "Reason required", description: "Enter a reason before returning." });
                  return;
                }
                returnMutation.mutate({ id: returnTargetId, reason: returnReason.trim() });
              }}
              disabled={returnMutation.isPending}
              data-testid="button-return-confirm"
            >
              {returnMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Return Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="bg-[#0d1b3e] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{scheduleTarget?.md_meeting_state ? "Reschedule MD review meeting" : "Schedule MD review meeting"}</DialogTitle>
            <DialogDescription className="text-white/60">
              {scheduleTarget?.md_meeting_state
                ? "Update the date, location, or attendees. All listed attendees with an email on file will be re-notified."
                : "Pick a date, time, and attendees. The action item itself stays in the SDF MD review queue and its due-date clock keeps running."}
            </DialogDescription>
          </DialogHeader>
          {scheduleTarget && (
            <div className="text-xs text-white/60 -mt-2">
              <div><span className="text-white/40">Item:</span> {scheduleTarget.title}</div>
              {scheduleTarget.md_meeting_state && (
                <div>
                  <span className="text-white/40">Currently scheduled:</span> {formatMeetingWhen(scheduleTarget.md_meeting_state.scheduledFor)}
                  {scheduleTarget.md_meeting_state.rescheduleCount > 0 && ` — already rescheduled ${scheduleTarget.md_meeting_state.rescheduleCount}x`}
                </div>
              )}
            </div>
          )}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm text-white/70">Date (required)</label>
                <Input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="input-schedule-date"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm text-white/70">Time (required)</label>
                <Input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="input-schedule-time"
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm text-white/70">Location (optional)</label>
              <Input
                value={scheduleLocation}
                onChange={(e) => setScheduleLocation(e.target.value)}
                placeholder="e.g., MD's office, Teams, Boardroom 2"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-schedule-location"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm text-white/70">Attendees ({scheduleAttendeeIds.size} selected)</label>
              <div className="max-h-48 overflow-y-auto rounded-md border border-white/10 bg-white/5 p-2 space-y-1">
                {activeUsersQuery.isLoading ? (
                  <div className="text-white/40 text-xs flex items-center gap-2 p-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading users...
                  </div>
                ) : activeUsers.length === 0 ? (
                  <div className="text-white/40 text-xs p-2">No active users found.</div>
                ) : (
                  activeUsers.map((u) => (
                    <label
                      key={u.id}
                      className="flex items-center gap-2 text-sm text-white/80 cursor-pointer p-1.5 rounded hover:bg-white/5"
                      data-testid={`row-attendee-${u.id}`}
                    >
                      <Checkbox
                        checked={scheduleAttendeeIds.has(u.id)}
                        onCheckedChange={() => toggleAttendee(u.id)}
                        data-testid={`checkbox-attendee-${u.id}`}
                      />
                      <span className="flex-1">{u.name || u.username}</span>
                      <span className={`text-xs ${u.email ? "text-white/40" : "text-amber-400/70"}`}>
                        {u.email || "no email on file"}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm text-white/70">Note (optional)</label>
              <Textarea
                value={scheduleNote}
                onChange={(e) => setScheduleNote(e.target.value)}
                placeholder="Optional context included in the notification email."
                className="bg-white/5 border-white/10 text-white"
                data-testid="textarea-schedule-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)} data-testid="button-schedule-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!scheduleTarget) return;
                if (!scheduleDate || !scheduleTime) {
                  toast({ variant: "destructive", title: "Date and time required", description: "Pick both a date and a time for the meeting." });
                  return;
                }
                const scheduleWhen = `${scheduleDate}T${scheduleTime}`;
                const scheduledForDate = new Date(scheduleWhen);
                if (isNaN(scheduledForDate.getTime())) {
                  toast({ variant: "destructive", title: "Invalid date/time", description: "Please re-enter the date and time." });
                  return;
                }
                if (scheduleAttendeeIds.size === 0) {
                  toast({ variant: "destructive", title: "Pick at least one attendee", description: "A meeting needs at least one attendee." });
                  return;
                }
                const attendees = activeUsers
                  .filter((u) => scheduleAttendeeIds.has(u.id))
                  .map((u) => ({ userId: u.id, name: u.name || u.username, email: u.email }));
                scheduleMutation.mutate({
                  id: scheduleTarget.id,
                  scheduledFor: scheduledForDate.toISOString(),
                  location: scheduleLocation.trim() || null,
                  attendees,
                  note: scheduleNote.trim() || undefined,
                });
              }}
              disabled={scheduleMutation.isPending}
              data-testid="button-schedule-confirm"
            >
              {scheduleMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {scheduleTarget?.md_meeting_state ? "Save reschedule" : "Schedule meeting"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={holdOpen} onOpenChange={setHoldOpen}>
        <DialogContent className="bg-[#0d1b3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Mark meeting as held</DialogTitle>
            <DialogDescription className="text-white/60">
              Records that the meeting actually happened. The action item itself stays in the queue until you approve, sign off, or return it.
            </DialogDescription>
          </DialogHeader>
          {holdTarget?.md_meeting_state && (
            <div className="text-xs text-white/60 -mt-2">
              <div><span className="text-white/40">Item:</span> {holdTarget.title}</div>
              <div><span className="text-white/40">Was scheduled:</span> {formatMeetingWhen(holdTarget.md_meeting_state.scheduledFor)}</div>
            </div>
          )}
          <div className="space-y-1">
            <label className="text-sm text-white/70">Outcome note (optional)</label>
            <Textarea
              value={holdNote}
              onChange={(e) => setHoldNote(e.target.value)}
              placeholder="Optional summary of the discussion."
              className="bg-white/5 border-white/10 text-white"
              data-testid="textarea-hold-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoldOpen(false)} data-testid="button-hold-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!holdTarget) return;
                markHeldMutation.mutate({ id: holdTarget.id, note: holdNote.trim() || undefined });
              }}
              disabled={markHeldMutation.isPending}
              data-testid="button-hold-confirm"
            >
              {markHeldMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Mark Held
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelMeetingOpen} onOpenChange={setCancelMeetingOpen}>
        <DialogContent className="bg-[#0d1b3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Cancel MD review meeting</DialogTitle>
            <DialogDescription className="text-white/60">
              The meeting slot will be cleared. The action item itself stays in the SDF MD queue. Attendees with an email on file will be notified.
            </DialogDescription>
          </DialogHeader>
          {cancelMeetingTarget?.md_meeting_state && (
            <div className="text-xs text-white/60 -mt-2">
              <div><span className="text-white/40">Item:</span> {cancelMeetingTarget.title}</div>
              <div><span className="text-white/40">Was scheduled:</span> {formatMeetingWhen(cancelMeetingTarget.md_meeting_state.scheduledFor)}</div>
            </div>
          )}
          <div className="space-y-1">
            <label className="text-sm text-white/70">Reason (optional)</label>
            <Textarea
              value={cancelMeetingNote}
              onChange={(e) => setCancelMeetingNote(e.target.value)}
              placeholder="Optional reason included in the cancellation email."
              className="bg-white/5 border-white/10 text-white"
              data-testid="textarea-cancel-meeting-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelMeetingOpen(false)} data-testid="button-cancel-meeting-cancel">
              Keep meeting
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!cancelMeetingTarget) return;
                cancelMeetingMutation.mutate({ id: cancelMeetingTarget.id, note: cancelMeetingNote.trim() || undefined });
              }}
              disabled={cancelMeetingMutation.isPending}
              data-testid="button-cancel-meeting-confirm"
            >
              {cancelMeetingMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Cancel Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dispatchOpen} onOpenChange={setDispatchOpen}>
        <DialogContent className="bg-[#0d1b3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Mark dispatched</DialogTitle>
            <DialogDescription className="text-white/60">
              Record where this item was sent upward and when. The system will calculate the working-day lag from MD approval.
            </DialogDescription>
          </DialogHeader>
          {dispatchTarget && (
            <div className="text-xs text-white/60 -mt-2">
              <div><span className="text-white/40">Item:</span> {dispatchTarget.title}</div>
              <div>
                <span className="text-white/40">MD approved:</span>{" "}
                {dispatchTarget.md_approved_at ? dispatchTarget.md_approved_at.slice(0, 10) : "\u2014"} — held {dispatchTarget.held_working_days} working day(s)
              </div>
            </div>
          )}
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm text-white/70">Recipient (required)</label>
              <Input
                value={dispatchRecipient}
                onChange={(e) => setDispatchRecipient(e.target.value)}
                placeholder="e.g., EDGE MD Office via email"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-dispatch-recipient"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm text-white/70">Dispatch date</label>
              <Input
                type="date"
                value={dispatchDate}
                onChange={(e) => setDispatchDate(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-dispatch-date"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm text-white/70">Note (optional)</label>
              <Textarea
                value={dispatchNote}
                onChange={(e) => setDispatchNote(e.target.value)}
                placeholder="Optional context, e.g., email subject line or message reference."
                className="bg-white/5 border-white/10 text-white"
                data-testid="textarea-dispatch-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDispatchOpen(false)} data-testid="button-dispatch-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!dispatchTarget || !dispatchRecipient.trim()) {
                  toast({ variant: "destructive", title: "Recipient required", description: "Enter where the item was sent." });
                  return;
                }
                dispatchMutation.mutate({
                  id: dispatchTarget.id,
                  recipient: dispatchRecipient.trim(),
                  note: dispatchNote.trim() || undefined,
                  dispatchedAt: dispatchDate,
                });
              }}
              disabled={dispatchMutation.isPending}
              data-testid="button-dispatch-confirm"
            >
              {dispatchMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm Dispatch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
